import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { AppConfigs, AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';

type Item = {
    name: string;
    value: number | string;
    icon: string;
    link: string;
};

const CustomSelectSetting4 = ({
    id,
    items,
    onTradeoffValueChange,
    disabled = false,
    isInitialRun = false,
    highlightedOptionNames = [],
}: {
    disabled?: boolean;
    importance: number;
    items: Item[];
    hideValues?: boolean;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    initialValues: Record<string, string>;
    id: string;
    criteriaConstraints: Record<string, CriteriaConstraint[]>;
    setCriteriaConstraints: (value: (((prevState: { [p: string]: CriteriaConstraint[] }) => {
        [p: string]: CriteriaConstraint[];
    }) | { [p: string]: CriteriaConstraint[] })) => void;
    variant?: AppConfigs['criteriaSettings']['enumerated'];
    isModal?: boolean;
    onCriteriaSettingsOpen?: (id: string) => void;
    isInitialRun?: boolean;
    highlightedOptionNames?: string[];
}) => {
    const [ selectedNames, setSelectedNames ] = useState<string[]>([]);

    useEffect(() => {
        if (isInitialRun) {
            // do not set selected names initially
            return;
        }

        const preselectedNames: string[] = [];
        items.forEach((item) => {
            if (+item.value === 100) {
                preselectedNames.push(item.name);
            }
        });

        setSelectedNames(preselectedNames);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isInitialRun ]);

    const updateValues = (nextSelectedNames: string[]) => {
        items.forEach((item) => {
            const value = nextSelectedNames.includes(item.name) ? 100 : 0;

            onTradeoffValueChange(value, id, item.name);
        });
    };

    const handleToggle = (item: Item) => {
        if (disabled) {
            return;
        }

        setSelectedNames((prev) => {
            const nextSelectedNames = prev.includes(item.name)
                ? prev.filter((name) => name !== item.name)
                : [ ...prev, item.name ];

            updateValues(nextSelectedNames);

            return nextSelectedNames;
        });
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 1500 }}>
            <Stack spacing={2}>
                {items.map((item) => {
                    const isSelected = selectedNames.includes(item.name);

                    return (
                        <Box
                            key={item.name}
                            onClick={() => handleToggle(item)}
                            role="checkbox"
                            className={highlightedOptionNames.includes(item.name) ? 'option-highlighted' : ''}
                            aria-checked={isSelected}
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleToggle(item);
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minHeight: { xs: 36, md: 82 },
                                px: { xs: 3, md: 4.5 },
                                borderRadius: '24px',
                                border: isSelected
                                    ? '4px solid #2b8c82'
                                    : '4px solid #d2d9e1',
                                backgroundColor: isSelected ? '#d8e9e7' : '#ffffff',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                opacity: disabled ? 0.6 : 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: isSelected ? '#2b8c82' : '#bfc9d4',
                                    transform: disabled ? 'none' : 'translateY(-1px)',
                                },
                            }}
                        >
                            {item.icon && (
                                <Box
                                    component="img"
                                    src={item.icon}
                                    alt={item.name}
                                    sx={{
                                        width: { xs: 24, md: 32 },
                                        height: { xs: 24, md: 32 },
                                        objectFit: 'contain',
                                        flexShrink: 0,
                                    }}
                                />
                            )}

                            <Typography
                                sx={{
                                    fontSize: { xs: 14, md: 18 },
                                    fontWeight: 700,
                                    color: '#1e2f46',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                }}
                                title={item.name}
                            >
                                {item.name}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default CustomSelectSetting4;
